import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  getTokenFromStorage,
  getUserFromStorage,
  updateAuthInStorage,
  clearAuthFromStorage,
  logout,
} from '@/utils/auth';
import { AuthContext } from '@/contexts/AuthContext';
import { getInitialJwt } from '@/utils/urlParams';
import type { AuthUser } from '@/utils/auth';

const AUTH_ROUTES = ['/login', '/signup'];

function isAuthRoute(pathname: string): boolean {
  return AUTH_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

/**
 * Resolves auth in priority order:
 *   1. `?jwt=` in the URL — Beatly platform handoff (magic link / cross-app).
 *      Exchanged server-side via POST /v1/auth/with-token, stored under the
 *      legacy `auth` localStorage key that coworkApi already falls back to.
 *   2. An existing Beatly session in the `auth` key.
 *   3. Supabase session (legacy — kept during the migration).
 * Redirects to /login if none resolve.
 */
export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'redirect'>('loading');
  const [user, setUser] = useState<AuthUser | undefined>(() => getUserFromStorage());

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // 1. Beatly ?jwt= handoff. initUrlParams() (main.tsx, pre-mount) already
      //    stripped it from the address bar and stashed it — read it from there,
      //    with a direct URL read as belt-and-braces.
      const url = new URL(window.location.href);
      const jwtParam = getInitialJwt() ?? url.searchParams.get('jwt');
      if (jwtParam) {
        if (url.searchParams.has('jwt')) {
          url.searchParams.delete('jwt');
          window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        }

        const beatlyUser = await exchangeBeatlyToken(jwtParam);
        if (cancelled) return;
        if (beatlyUser) {
          // Drop any stale Supabase session so its token can't shadow the
          // Beatly one in getTokenFromStorage (SIGNED_OUT handler below
          // knows to leave Beatly sessions alone).
          void supabase.auth.signOut().catch(() => {});
          setUser(beatlyUser);
          setStatus('authenticated');
          if (isAuthRoute(window.location.pathname)) navigate('/cowork', { replace: true });
          return;
        }
        // Invalid/expired handoff token — fall through to the other paths
      }

      // 2. Existing Beatly session (no Supabase session will exist for these users)
      const stored = getUserFromStorage();
      const storedToken = getTokenFromStorage();
      if (stored && storedToken && isBeatlyUser(stored)) {
        setUser(stored);
        setStatus(isAuthRoute(pathname) ? 'redirect' : 'authenticated');
        if (isAuthRoute(pathname)) navigate('/cowork', { replace: true });
        return;
      }

      // 3. Legacy Supabase session
      const { data: { session } } = await supabase.auth.getSession();

      if (isAuthRoute(pathname)) {
        if (session?.user) {
          // Already logged in, redirect to app
          const authUser = mapSupabaseUser(session.user);
          updateAuthInStorage({ token: session.access_token, user: authUser });
          setUser(authUser);
          setStatus('redirect');
          navigate('/cowork', { replace: true });
        } else {
          setStatus('authenticated'); // Show login page
        }
        return;
      }

      if (!session?.user) {
        if (cancelled) return;
        setStatus('redirect');
        navigate('/login', { replace: true });
        return;
      }

      // Authenticated — set user
      const authUser = mapSupabaseUser(session.user);
      updateAuthInStorage({ token: session.access_token, user: authUser });
      if (cancelled) return;
      setUser(authUser);
      setStatus('authenticated');
    }

    init();

    // Listen for auth state changes (e.g. magic link callback, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (cancelled) return;

      if (event === 'SIGNED_IN' && session?.user) {
        const authUser = mapSupabaseUser(session.user);
        updateAuthInStorage({ token: session.access_token, user: authUser });
        setUser(authUser);
        setStatus('authenticated');
        if (isAuthRoute(window.location.pathname)) {
          navigate('/cowork', { replace: true });
        }
      } else if (event === 'SIGNED_OUT') {
        // A Beatly-authenticated session doesn't live in Supabase — don't let
        // a Supabase sign-out (e.g. clearing a stale session) log it out.
        const current = getUserFromStorage();
        if (current && isBeatlyUser(current)) return;
        clearAuthFromStorage();
        setUser(undefined);
        setStatus('redirect');
        navigate('/login', { replace: true });
      } else if (event === 'TOKEN_REFRESHED' && session) {
        updateAuthInStorage({ token: session.access_token });
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [pathname, navigate]);

  if (status === 'redirect') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="text-[#667085] text-[15px]">Redirecting to sign in…</div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="text-[#667085] text-[15px]">Signing you in…</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Helpers ──────────────────────────────────────────────────────

/** Beatly platform user ids end in "-usr" (or "admin-…" for backoffice staff). */
function isBeatlyUser(user: AuthUser): boolean {
  return user.user_id.endsWith('-usr') || user.user_id.startsWith('admin-');
}

/**
 * Exchange a Beatly handoff JWT for a validated token + user via the backend
 * proxy (the browser can't call api-v2 directly until CORS registration lands).
 */
async function exchangeBeatlyToken(jwt: string): Promise<AuthUser | undefined> {
  const apiUrl = import.meta.env.VITE_COWORK_API_URL || '';

  // Preferred: exchange via api-v2 (validates magic-link tokens, re-mints)
  try {
    const res = await fetch(`${apiUrl}/v1/auth/with-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: jwt }),
    });
    if (res.ok) {
      const data = (await res.json()) as { token?: string; user?: AuthUser };
      if (data.token && data.user) {
        updateAuthInStorage({ token: data.token, user: data.user });
        return data.user;
      }
    }
  } catch { /* fall through */ }

  // Fallback: the token may already be a user token our backend trusts
  // (api-v2 exchange unreachable, or a directly-minted token)
  try {
    const res = await fetch(`${apiUrl}/v1/auth/me`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (!res.ok) return undefined;
    const data = (await res.json()) as { user?: AuthUser };
    if (!data.user) return undefined;
    updateAuthInStorage({ token: jwt, user: data.user });
    return data.user;
  } catch {
    return undefined;
  }
}

function mapSupabaseUser(supaUser: { id: string; email?: string; user_metadata?: Record<string, unknown> }): AuthUser {
  const meta = supaUser.user_metadata || {};
  return {
    user_id: supaUser.id,
    name: (meta.full_name as string) || (meta.name as string) || supaUser.email?.split('@')[0] || 'User',
    email: supaUser.email,
    is_client: true,
    // Team will be resolved by the backend from the JWT
  };
}
